import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useMediaQuery } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { change, Field, FieldArray, reduxForm } from 'redux-form';

import { Typography } from '@material-ui/core';

import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import HotelOutlinedIcon from '@material-ui/icons/HotelOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

import { ACCOMMODATION, BOOK_DATES } from 'graphql/queries/accommodations';
import {
  CREATE_ACCOMMODATION_BOOKING,
  UPDATE_ACCOMMODATION_BOOKING
} from 'graphql/mutations/accommodations-bookings';

import {
  AppHeader,
  CircularLoader,
  DropDown,
  TextIconButton,
  CheckboxButtonsGroup,
  FormActionButtons,
  BookInfoList,
  CustomDateRangePicker,
  BookActivityDialog
} from 'components';

import ConfirmContext from 'contexts/ConfirmContext';

import { getListOfAvailableCurrencies } from 'utils/book-accommodation-and-activity/currency';
import { getReceivedAccommodationData } from 'utils/accommodations-and-activities/prepareReceivedAccommodationData';
import { getParticipants } from 'utils/book-accommodation/prepareParticipants';
import { getPreparedBookAccommodationData } from 'utils/book-accommodation/prepareBookAccommodationData';
import {
  getAvailableRangeDates,
  getDisabledDates
} from 'utils/book-accommodation/availableDates';
import { getFormByCurrentLanguage } from 'utils/form-localization/formLocalization';

import NumberOfParticipants from 'pages/book-activity/components/number-of-participants/NumberOfParticipants';
import AddOnsList from 'pages/book-activity/components/add-ons-list/AddOnsList';
import GuestInformation from 'pages/book-activity/components/guest-information/GuestInformation';
import BookingSummary from 'pages/book-activity/components/booking-summary/BookingSummary';
import BookRelatedField from 'pages/book-activity/components/book-related-field/BookRelatedField';

import validate from './validate';

import useStyles from './styles';

const initialValues = {
  maxParticipants: 0,
  currency: 'NOK',
  participants: [],
  addOns: [],
  mandatoryFees: [],
  activities: [],
  pay: {
    'Pay on arrival': true
  },
  minNights: 1
};

const FIXED_PRICES = 'pricesFixedForm';
const CATEGORY_PRICES = 'pricesCategoryForm';
const DAILY_PRICES = 'pricesDailyForm';
const FUTURE_PRICES = 'futurePricesForm';

const BookAccommodation = ({
  bookAccommodationForm,
  changeFormFieldValue,
  handleSubmit,
  bookedData,
  isUpdate,
  businessOwner
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);

  const selectedLanguage = i18n.language;
  const ownerId =
    businessOwner.selectedBusinessOwner.ownerId ||
    businessOwner.selectedBusinessOwner.id;

  const accommodationId = bookAccommodationForm.values.accommodationId;
  const initialDates = bookedData ? bookedData.dates : null;
  const startDate = initialDates ? initialDates.startDate : moment();  

  const [deletedRelatedActivities, setDeletedRelatedActivity] = React.useState(
    []
  );
  const [relatedItem, setRelatedItem] = React.useState(null);
  const [isOpenBookDialog, setOpenBookDialog] = React.useState(false);
  const [isLoading, setLoading] = React.useState(null);
  const [accommodation, setAccommodation] = React.useState(null);
  const [isUpdatedParticipants, setIsUpdatedParticipants] = React.useState(
    false
  );
  const [listOfCurrencies, setListOfCurrencies] = React.useState([]);

  const [createAccommodationBooking] = useMutation(
    CREATE_ACCOMMODATION_BOOKING
  );
  const [updateAccommodationBooking] = useMutation(
    UPDATE_ACCOMMODATION_BOOKING
  );

  const { loading, data } = useQuery(ACCOMMODATION, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: accommodationId || parseInt(id),
      businessOwnerId: ownerId
    }
  });

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useLayoutEffect(() => {
    const updateSize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);



  const {
    loading: bookDatesLoading,
    data: bookDatesData,
    refetch: bookDatesRefetch
  } = useQuery(BOOK_DATES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      accommodationId: accommodationId || parseInt(id),
      businessOwnerId: ownerId,
      startPeriod: moment(startDate)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(startDate).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    }
  });

  React.useEffect(() => {
    if (data && data.accommodations && data.accommodations.length) {
      const receivedAccommodation = data.accommodations[0];
      const accommodationData = getReceivedAccommodationData(
        receivedAccommodation
      );

      if (receivedAccommodation.futurePrices?.[0]?.ratePlanPrices) {
        accommodationData['pricesType'] = FUTURE_PRICES;
      } else if (receivedAccommodation.prices[0]?.participant.participant) {
        accommodationData['pricesType'] = CATEGORY_PRICES;
      } else if (receivedAccommodation.prices[0]?.day) {
        accommodationData['pricesType'] = DAILY_PRICES;
      } else  {
        accommodationData['pricesType'] = FIXED_PRICES;
      }

      changeFormFieldValue(
        'minNights',
        accommodationData.generalForm.minNights
      );

      setListOfAvailableCurrencies(receivedAccommodation);
      setMaxParticipants(accommodationData);
      setParticipants(accommodationData);
      setAddOns(accommodationData);
      setMandatoryFees(accommodationData);

      setAccommodation(accommodationData);
    }
  }, [data]);

  React.useEffect(() => {
    if (!bookAccommodationForm || !accommodation) return;
    setParticipants(accommodation);
  }, [
    bookAccommodationForm.values.currency,
    bookAccommodationForm.values.dates
  ]);

  const changeMonthHandler = date => {
    bookDatesRefetch({
      accommodationId: accommodationId || parseInt(id),
      startPeriod: moment(date)
        .startOf('month')
        .format('YYYY-MM-DD'),
      endPeriod: moment(moment(date).add(1, 'months'))
        .endOf('month')
        .format('YYYY-MM-DD')
    });
  };

  const setListOfAvailableCurrencies = receivedAccommodation => {
   const listOfAvailableCurrencies = getListOfAvailableCurrencies(
     receivedAccommodation.prices ||  receivedAccommodation.futurePrices
   );
    
    const selectedCurrency = bookAccommodationForm.values.currency;   

    setListOfCurrencies(listOfAvailableCurrencies);

    if (!listOfAvailableCurrencies.includes(selectedCurrency)) {
      changeFormFieldValue('currency', listOfAvailableCurrencies[0]);
    }
  };

  const setParticipants = accommodation => {
    setIsUpdatedParticipants(!isUpdatedParticipants);

    const currency = bookAccommodationForm.values.currency;
    const dates = bookAccommodationForm.values.dates;
    const previousParticipants =
      bookAccommodationForm.values.participants || [];

    if (dates && dates.startDate && dates.endDate) {
      const participants = getParticipants(
        accommodation,
        currency,
        previousParticipants,
        dates
      );
      changeFormFieldValue('participants', participants);
    } else {
      changeFormFieldValue('participants', []);
    }
  };

  const setAddOns = accommodation => {
    const previousAddOns = bookAccommodationForm.values.addOns || [];

    const addOnsList = accommodation.addOnsForm.addOns.map(item => {
      const theSameAddOn = previousAddOns.find(addOn => addOn.id === item.id);
      return {
        ...item,
        value: theSameAddOn ? theSameAddOn.value : 0,
        originalId: theSameAddOn ? theSameAddOn.originalId : null
      };
    });

    changeFormFieldValue('addOns', addOnsList);
  };

  const setMandatoryFees = accommodation => {
    const previousMandatoryFees = bookAccommodationForm.values.mandatoryFees || [];

    const mandatoryFeesList = accommodation.mandatoryFeesForm.mandatoryFees.map(item => {
      const theSameMandatoryFee = previousMandatoryFees.find(mandatoryFee => mandatoryFee.id === item.id);
      return {
        ...item,
        value: theSameMandatoryFee ? theSameMandatoryFee.value : 0,
        originalId: theSameMandatoryFee ? theSameMandatoryFee.originalId : null
      };
    });

    changeFormFieldValue('mandatoryFees', mandatoryFeesList);
  };


  const setMaxParticipants = accommodation => {
    changeFormFieldValue('maxParticipants', accommodation.generalForm.guests);
  };

  const handlerSubmitWrapper = props => {
    if (bookAccommodationForm.syncErrors) {
      const syncErrors = bookAccommodationForm.syncErrors;
      const errorsLength = Object.keys(syncErrors).length;

      if (syncErrors.isMaxParticipantsErrors && errorsLength === 2) {
        const error = syncErrors.participants['_error'];
        const errorSplit = error.split(' ');
        const errorLength = errorSplit.length;
        enqueueSnackbar(
          t('The number of Participants can’t exceed', {
            number: errorSplit[errorLength - 1]
          }),
          {
            variant: 'warning'
          }
        );
      } else {
        enqueueSnackbar(t('Please fill in all the required fields'), {
          variant: 'warning'
        });
      }
    }

    handleSubmit(submit)(props);
  };

  const submit = () => {
    setLoading(true);

    let data = getPreparedBookAccommodationData(
      accommodation,
      bookAccommodationForm,
      deletedRelatedActivities
    );

    if (isUpdate) {
      updateAccommodationBooking({ variables: data }).then(
        () => {
          enqueueSnackbar(
            t('Accommodation booking has been successfully updated'),
            {
              variant: 'success'
            }
          );
          history.push({
            pathname: '/bookings-list'
          });
        },
        (error) => {
          setLoading(false);
        // Extract error message from GraphQL response
        const errorMessage =
          error?.graphQLErrors?.[0]?.message ||
          t('Something was wrong'); // Fallback to a generic error message if no specific error message exists

        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      }
      );
    } else {
      createAccommodationBooking({ variables: data }).then(
        () => {
          enqueueSnackbar(t('Accommodation has been successfully booked'), {
            variant: 'success',
          });
          history.push({
            pathname: '/accommodations',
          });
        },
        (error) => {
          setLoading(false);
        // Extract error message from GraphQL response
        const errorMessage =
          error?.graphQLErrors?.[0]?.message ||
          t('Something was wrong'); // Fallback to a generic error message if no specific error message exists

        enqueueSnackbar(errorMessage, {
          variant: 'error',
        });
      }
      );
    }
  };

  const cancelRelatedActivity = () => {
    confirm({
      title: 'You have unsaved changes',
      description: t('Are you sure you want to cancel?'),
      confirmationText: 'Cancel',
      cancellationText: 'Back'
    })
      .then(() => setOpenBookDialog(false))
      .catch(() => {});
  };

  const addRelatedActivity = activity => {
    setOpenBookDialog(false);
    changeFormFieldValue('activities', [
      ...bookAccommodationForm.values.activities,
      activity
    ]);
  };

  const updateRelatedActivity = activity => {
    setOpenBookDialog(false);
    changeFormFieldValue('activities', [
      ...bookAccommodationForm.values.activities.map((item, index) => {
        if (index === activity.index) {
          return activity;
        }
        return item;
      })
    ]);
  };

  const getBookRelatedField = () => {
    const activities = bookAccommodationForm.values.activities;

    return (
      <div>
        {activities.map((item, index) => (
          <BookRelatedField
            key={index}
            title={
              getFormByCurrentLanguage(
                selectedLanguage,
                item,
                item['Norwegian']
              ).title
            }
            addOns={item.addOns}
            mandatoryFees={item.MandatoryFees}
            deleteCallBack={() => deleteRelatedItem(item, index)}
            editCallBack={() => {
              setRelatedItem({ ...item, index: index });
              setOpenBookDialog(true);
            }}
          />
        ))}
      </div>
    );
  };

  const deleteRelatedItem = (deleteItem, deleteItemIndex) => {
    confirm({
      title: 'Are you sure you want to delete it?',
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        const activities = bookAccommodationForm.values.activities;
        changeFormFieldValue('activities', [
          ...activities.filter((item, index) => index !== deleteItemIndex)
        ]);

        if (deleteItem.id) {
          setDeletedRelatedActivity([
            ...deletedRelatedActivities,
            deleteItem.id
          ]);
        }
      })
      .catch(() => {});
  };

  const deleteAllRelatedItems = () => {
    confirm({
      title: 'Are you sure you want to delete it?',
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        const activities = bookAccommodationForm.values.activities;
        const deletedActivitiesIds = [];

        activities.map(deleteItem => {
          if (deleteItem.id) {
            deletedActivitiesIds.push(deleteItem.id);
          }
        });

        setDeletedRelatedActivity([
          ...deletedRelatedActivities,
          ...deletedActivitiesIds
        ]);

        changeFormFieldValue('activities', []);
      })
      .catch(() => {});
  };

  const generalForm = accommodation
    ? getFormByCurrentLanguage(
        selectedLanguage,
        accommodation.generalForm,
        accommodation['generalForm-Norwegian']
      )
    : {};
  const descriptionForm = accommodation
    ? getFormByCurrentLanguage(
        selectedLanguage,
        accommodation.descriptionForm,
        accommodation['descriptionForm-Norwegian']
      )
    : {};

  return (
    <>
      <AppHeader />

      {accommodation && isOpenBookDialog && (
        <BookActivityDialog
          open={isOpenBookDialog}
          ownerId={ownerId}
          onCancel={cancelRelatedActivity}
          onConfirm={addRelatedActivity}
          onUpdate={updateRelatedActivity}
          relatedActivitiesForm={accommodation.relatedActivitiesForm}
          currency={bookAccommodationForm.values.currency}
          initialValues={
            relatedItem || {
              participants: [],
              addOns: [],
              mandatoryFees: []
            }
          }
        />
      )}
      <CircularLoader
        isLoading={loading || bookDatesLoading || isLoading }
        loaderClassName={classes.loaderClassName}
        className={classes.loaderWrapper}
      />

      {accommodation && (
        <div className={classes.root}>
          <div className={classes.leftSide}>
            <Typography variant="h3" classes={{ root: classes.productCode }}>
              {t('Product code')}: {accommodation.generalForm.productCode}
            </Typography>

            <div
              className={classes.photo}
              style={{
                backgroundImage: `url(${accommodation.generalForm.coverPhoto.image})`
              }}
            >
              {''}
            </div>

            <Typography variant="h2" className={classes.fontWeight}>
              {generalForm.title}
            </Typography>

            <div className={classes.listInfo}>
              <div className={classes.infoItem}>
                <LocationOnOutlinedIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {accommodation.generalForm.location}
                </Typography>
              </div>
              <div className={classes.infoItem}>
                <PeopleOutlineIcon classes={{ root: classes.infoIcon }} />
                <Typography variant="body1">
                  {accommodation.generalForm.guests} {t('Guests')}
                </Typography>
              </div>
              {generalForm.bedDetails && !!generalForm.bedDetails.length && (
                <div className={classes.infoItem}>
                  <HotelOutlinedIcon classes={{ root: classes.infoIcon }} />
                  <Typography variant="body1">
                    {generalForm.bedDetails.join(' • ')}
                  </Typography>
                </div>
              )}
            </div>

            <Typography variant="body1" classes={{ root: classes.overview }}>
              {descriptionForm.overview}
            </Typography>

            <BookInfoList
              activity={accommodation}
              currency={bookAccommodationForm.values.currency}
              defaultExpanded={true}
            />
          </div>

          <div className={classes.rightSide}>
            <form
              onSubmit={handlerSubmitWrapper}
              className={classes.bookWrapper}
            >
              <Typography
                variant="h1"
                className={classNames(classes.fontWeight, classes.inputIndent)}
              >
                {isUpdate
                  ? t('Update Accommodation Booking')
                  : t('Book Accommodation')}
              </Typography>

              <Field
                name="currency"
                className={classNames(
                  classes.currencyInput,
                  classes.sectionIndent
                )}
                list={listOfCurrencies}
                label="Select currency"
                component={DropDown}
              />
              
              {/* <Field
                name="dates"
                // availableRange={getAvailableRangeDates(accommodation)}
                disabledDates={getDisabledDates(bookDatesData, initialDates)}
                changeMonthsCallback={changeMonthHandler}
                className={classes.inputIndent}
                inputClassName={classes.disableInputFocus}
                label="Select dates *"
                initialFocus="startDate"
                dateRangePickerClass="dateRangeOpenedPicker"
                daySize={39}
                component={CustomDateRangePicker}
                updateBooking
                isStartDateRenderMonth={!!initialDates}
              /> */}
              <Field
                name="dates"
                disabledDates={getDisabledDates(bookDatesData, initialDates)}
                changeMonthsCallback={changeMonthHandler}
                className={isSmallScreen ? classes.smallInputIndent : isMediumScreen ? classes.mediumInputIndent : classes.inputIndent}
                inputClassName={classes.disableInputFocus}
                label="Select dates *"
                initialFocus="startDate"
                dateRangePickerClass="dateRangeOpenedPicker"
                // numberOfMonths={isSmallScreen ? 1 : 2}
                // daySize={isSmallScreen ? 30 : 39}
                numberOfMonths={windowWidth > 1010 ? 2 : 1 || windowWidth <= 750 ? 1 : 2 }
                daySize={windowWidth > 600 ? 38 : 41}
                component={CustomDateRangePicker}
                updateBooking
                isStartDateRenderMonth={!!initialDates}
              />

              {!!bookAccommodationForm.values.participants.length && (
                <FieldArray
                  name="participants"
                  title="Number of guests *"
                  isUpdatedParticipants={isUpdatedParticipants}
                  component={NumberOfParticipants}
                />
              )}

              {!!bookAccommodationForm.values.addOns.length && (
                <FieldArray
                  name="addOns"
                  title="Add-ons"
                  currency={bookAccommodationForm.values.currency}
                  component={AddOnsList}
                  addOnDescClass={classes.addOnDescClass}
                  addOnTitle={classes.addOnTitle}
                />
              )}


           

              <div className={classes.sectionIndent}>
                <Typography variant="h2" className={classes.activitiesTitle}>
                  {t('Activities')}
                </Typography>
                <div className={classes.activitiesButtons}>
                  <TextIconButton
                    icon={<AddCircleOutlineIcon />}
                    text={t('Book')}
                    click={() => {
                      setRelatedItem(null);
                      setOpenBookDialog(true);
                    }}
                  />
                  {!!bookAccommodationForm.values.activities.length && (
                    <TextIconButton
                      icon={<DeleteOutlineIcon />}
                      text={t('Delete all')}
                      click={deleteAllRelatedItems}
                    />
                  )}
                </div>
                {!!bookAccommodationForm.values.activities.length && (
                  <div className={classes.relatedFieldWrapper}>
                    {getBookRelatedField()}
                  </div>
                )}
              </div>

              <GuestInformation />

              <div className={classes.paymentWrapper}>
                <Typography variant="h2" className={classes.payment}>
                  {t('Payment')}
                </Typography>
                <Field
                  name="pay"
                  className={classes.paymentField}
                  isDisabledChange={true}
                  disabledDays={['Pay on arrival']}
                  component={CheckboxButtonsGroup}
                />
              </div>

              <BookingSummary
                relatedItems={bookAccommodationForm.values.activities}
                activity={accommodation}
                bookActivity={bookAccommodationForm.values}
                generalSectionTitle="Accommodation"
              />

              <FormActionButtons
                cancelPath={isUpdate ? '/bookings-list' : '/accommodations'}
                actionLabel={isUpdate ? 'Update' : 'Book'}
              />
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = state => {
  return {
    bookAccommodationForm: state.form.bookAccommodationForm,
    businessOwner: state.businessOwner
  };
};

const mapDispatchToProps = dispatch => ({
  changeFormFieldValue: (field, value) => {
    dispatch(change('bookAccommodationForm', field, value));
  }
});

export default reduxForm({
  form: 'bookAccommodationForm',
  destroyOnUnmount: true,
  touchOnBlur: false,
  validate,
  initialValues
})(connect(mapStateToProps, mapDispatchToProps)(BookAccommodation));
